import { Link, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/lib/cart";
import { formatINR } from "@/lib/format";

export const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();

  const goToCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="flex w-full flex-col border-l border-[color:var(--acx-border)] bg-[var(--acx-bg-2)] p-0 text-white sm:max-w-md"
        data-testid="cart-drawer"
      >
        <SheetHeader className="border-b border-[color:var(--acx-border)] p-5 text-left">
          <SheetTitle className="flex items-center gap-2 text-white">
            <ShoppingBag size={18} /> Your Cart ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8 text-center">
            <ShoppingBag size={48} className="text-white/20" />
            <p className="text-white/60">Your cart is empty.</p>
            <Link
              to="/shop"
              onClick={() => setOpen(false)}
              className="inline-flex items-center gap-2 rounded-[12px] bg-white px-5 py-3 text-[14px] font-semibold text-black"
            >
              Start Shopping <ArrowRight size={16} />
            </Link>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-5">
              <div className="space-y-4">
                {items.map((item) => (
                  <div key={item.key} data-testid="cart-item" className="flex gap-3 rounded-[14px] border border-[color:var(--acx-border)] bg-[var(--acx-card)] p-3">
                    <img src={item.image} alt={item.name} className="h-20 w-20 rounded-[10px] object-cover" />
                    <div className="flex flex-1 flex-col">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="text-[14px] font-semibold leading-tight">{item.name}</p>
                          <p className="mt-0.5 text-[12px] text-white/50">{item.variant}</p>
                        </div>
                        <button onClick={() => removeItem(item.key)} aria-label="Remove" className="text-white/40 transition-colors hover:text-[#E11B22]">
                          <Trash2 size={16} />
                        </button>
                      </div>
                      <div className="mt-auto flex items-center justify-between pt-2">
                        <div className="inline-flex items-center rounded-[10px] border border-[color:var(--acx-border)]">
                          <button className="px-2.5 py-1.5 text-white/70 hover:text-white" onClick={() => updateQty(item.key, item.qty - 1)} aria-label="Decrease"><Minus size={13} /></button>
                          <span className="w-7 text-center text-[13px]">{item.qty}</span>
                          <button className="px-2.5 py-1.5 text-white/70 hover:text-white" onClick={() => updateQty(item.key, item.qty + 1)} aria-label="Increase"><Plus size={13} /></button>
                        </div>
                        <span className="text-[14px] font-semibold">{formatINR(item.price * item.qty)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t border-[color:var(--acx-border)] p-5">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-[14px] text-white/60">Subtotal</span>
                <span className="text-xl font-semibold">{formatINR(subtotal)}</span>
              </div>
              <button
                data-testid="cart-checkout-button"
                onClick={goToCheckout}
                className="flex w-full items-center justify-center gap-2 rounded-[12px] bg-[#E11B22] px-5 py-3.5 text-[14px] font-semibold text-white transition-colors hover:bg-[#c9151b]"
              >
                Proceed to Checkout <ArrowRight size={16} />
              </button>
              <p className="mt-3 text-center text-[12px] text-white/40">Free shipping on orders above {formatINR(499)}</p>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
